import {
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactElement,
  type ReactNode,
  type Ref,
  useId,
  useRef,
} from 'react';

import styles from './Drawer.module.css';
import { Icon } from './Icon';
import { IconButton } from './IconButton';
import type { IconName } from './iconName';

/**
 * A NON-MODAL tabbed drawer — ADR-0101 §7.15, §8.2–8.5.
 *
 * A bottom sheet on phones, a 384px column beside the editor from 960px, and the same DOM
 * either way. It is generic `ui/**`: it knows about tabs, a close control and a panel, and
 * nothing at all about problems or repairs. `src/app/` fills it.
 *
 * ═══ NON-MODAL, AND EVERY WORD OF THAT IS A DECISION ═══
 *
 * No `<dialog>`, no `showModal()`, no scrim, no focus trap, no `inert` on the editor. A user
 * reading a diagnostic has to be able to look at the line it points to, click into the code,
 * fix it and watch the row disappear — all with the list still on screen. A modal forbids
 * exactly that, and a modal bottom sheet on a phone forbids it while also stealing the back
 * gesture. So this is an `<aside>` with a `hidden` attribute, which is as much machinery as
 * "a region you can show and hide" actually needs.
 *
 * What it therefore owes instead of a trap: Escape closes it from anywhere inside (§8.2), and
 * the OWNER puts focus back on the control that opened it. That half cannot live here — the
 * door is in the header, three components away — so `onClose` is the whole contract and
 * `App.tsx` holds the ref.
 *
 * ═══ WHY `hidden` AND NOT CONDITIONAL RENDERING ═══
 *
 * The drawer is `aria-controls`' target. A relationship to an element that does not exist is
 * a broken relationship: a screen reader that follows `aria-controls` from a collapsed door
 * would find nothing, and `e2e/a11y.spec.ts` asserts the target is attached before it is ever
 * opened. `hidden` also means the list's scroll position and the roving tab index survive a
 * close-and-reopen, which is what a user who closed the sheet to look at line 40 expects.
 *
 * ═══ A REAL tablist ═══
 *
 * `role="tab"` promises arrow keys and ONE tab stop, so both are implemented: ← / → / Home /
 * End, and `tabIndex={-1}` on everything not selected. Selection follows focus, because both
 * panels are already-built React children and moving between them cannot be expensive enough
 * to justify the "activate manually" variant of the pattern. `ui/shell/TabBar.tsx` makes the
 * same argument for the view strip; the two are deliberately not shared, because that one is a
 * strip of destinations laid out by the shell grid and this one is a head inside a floating
 * surface, and folding them together would mean a component with two layouts and two roles.
 */

export interface DrawerTabCount {
  readonly value: number;
  /**
   * Which pill the count wears — ADR-0101 §7.15's table. It is a TONE and not a hue, because
   * the caller is naming the meaning ("these are errors") and this component owns the
   * translation from meaning to family; `quiet` is the zero-ish state that must not shout.
   */
  readonly tone: 'danger' | 'warn' | 'repair' | 'quiet';
}

export interface DrawerTab {
  readonly id: string;
  /** Already localised — `ui/**` does not translate (ADR-0003). */
  readonly label: string;
  readonly icon: IconName;
  readonly count?: DrawerTabCount;
}

export interface DrawerProps {
  /** The DOM id the door's `aria-controls` points at. */
  readonly id: string;
  readonly open: boolean;
  /** Localised name of the region — "Diagnostics". */
  readonly label: string;
  /** Localised accessible name of the × — "Hide problems". */
  readonly closeLabel: string;
  readonly tabs: readonly DrawerTab[];
  readonly activeTab: string;
  readonly onTabChange: (id: string) => void;
  readonly onClose: () => void;
  readonly children: ReactNode;
  /**
   * The SELECTED tab's element. `App.tsx` focuses it when the door opens the drawer, because
   * the drawer is far from the door in DOM order and a keyboard user who pressed the door
   * would otherwise still be standing in the header (§8.2).
   */
  readonly tabRef?: Ref<HTMLButtonElement>;
  readonly ref?: Ref<HTMLElement>;
}

export function Drawer({
  id,
  open,
  label,
  closeLabel,
  tabs,
  activeTab,
  onTabChange,
  onClose,
  children,
  tabRef,
  ref,
}: DrawerProps): ReactElement {
  const generatedId = useId();
  const head = useRef<HTMLDivElement | null>(null);

  const tabDomId = (tabId: string): string => `${generatedId}tab-${tabId}`;
  const panelDomId = (tabId: string): string => `${generatedId}panel-${tabId}`;

  const selected = tabs.find((tab) => tab.id === activeTab) ?? tabs[0];

  const move = (from: number, delta: number): void => {
    if (tabs.length === 0) return;
    const length = tabs.length;
    const next = tabs[(((from + delta) % length) + length) % length];
    if (next === undefined) return;
    onTabChange(next.id);
    // Focus follows selection, so the roving `tabIndex` and DOM focus cannot disagree. Found
    // by id rather than through a ref map: there is one head, the ids are derived two lines
    // above, and a `Map<string, HTMLElement>` would be a second thing to keep in step.
    head.current?.ownerDocument.getElementById(tabDomId(next.id))?.focus();
  };

  const onTabKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>): void => {
    const current = tabs.findIndex((tab) => tab.id === activeTab);
    if (current < 0) return;

    switch (event.key) {
      case 'ArrowRight':
        move(current, 1);
        break;
      case 'ArrowLeft':
        move(current, -1);
        break;
      case 'Home':
        move(0, 0);
        break;
      case 'End':
        move(tabs.length - 1, 0);
        break;
      default:
        return;
    }
    // Only after a key that was actually handled: swallowing Home/End unconditionally would
    // break the document-start shortcut for anyone who reached the head by accident.
    event.preventDefault();
  };

  return (
    <aside
      aria-label={label}
      className={styles.drawer}
      // `data-surface`, not a class: under forced colours every tinted surface loses the tone
      // that separated it, and `ui/styles/base.css` states the "give it a CanvasText edge"
      // rule once, for `[data-surface]`.
      data-surface="panel"
      hidden={!open}
      id={id}
      onKeyDown={(event) => {
        // Escape from ANYWHERE inside — a row, a Fix button, the tab head (§8.2). It is not
        // `onKeyDownCapture`: an inner control that genuinely owns Escape (none today, a
        // filter box tomorrow) must be able to handle it first and stop the bubble.
        if (event.key !== 'Escape') return;
        event.preventDefault();
        onClose();
      }}
      ref={ref}
    >
      {/* Phones only. A grab handle is what makes a sheet look draggable, and §8.5 keeps the
          drag itself as an optional P2; this is the affordance, hidden from 960px where the
          drawer is a column and there is nothing to drag. */}
      <div aria-hidden="true" className={styles.grab} />

      <div className={styles.head}>
        <div
          aria-label={label}
          className={styles.tabs}
          onKeyDown={onTabKeyDown}
          ref={head}
          role="tablist"
        >
          {tabs.map((tab) => {
            const isSelected = tab.id === selected?.id;
            return (
              <button
                /*
                 * ONLY THE SELECTED TAB CLAIMS A PANEL, because only the selected tab has one.
                 *
                 * One tabpanel is rendered below — the unselected tab's panel element does not
                 * exist — so `aria-controls` on the other tab would point at an id that is in
                 * the document at no moment of the drawer's life, open or closed. That is the
                 * broken relationship this file's header argues against for the door, and
                 * axe's `aria-valid-attr-value` flags it. `aria-controls` is optional on
                 * `role="tab"` (ARIA 1.2 lists it as supported, not required), so the honest
                 * reading is the one that says nothing rather than the one that points at
                 * nothing. Selection follows focus here, so the tab a user is standing on is
                 * always the one that does carry the relationship.
                 */
                {...(isSelected ? { 'aria-controls': panelDomId(tab.id) } : {})}
                aria-selected={isSelected}
                className={styles.tab}
                id={tabDomId(tab.id)}
                key={tab.id}
                onClick={() => {
                  onTabChange(tab.id);
                }}
                ref={isSelected ? tabRef : undefined}
                role="tab"
                tabIndex={isSelected ? 0 : -1}
                type="button"
              >
                {/* The glyph repeats the word beside it, so it is decoration — `Icon` is
                    `aria-hidden` by construction. */}
                <Icon className={styles.tabIcon} name={tab.icon} />
                {tab.label}
                {tab.count === undefined ? null : (
                  // `aria-hidden`: the number is already in the tab's own text for anyone
                  // reading it, and a count that changed on every keystroke would re-announce
                  // the tab on each one (the same rule the Fix-all badge follows).
                  <span aria-hidden="true" className={styles.count} data-tone={tab.count.tone}>
                    {tab.count.value}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <IconButton className={styles.close} icon="close" label={closeLabel} onClick={onClose} />
      </div>

      {selected === undefined ? null : (
        <div
          aria-labelledby={tabDomId(selected.id)}
          className={styles.panel}
          id={panelDomId(selected.id)}
          role="tabpanel"
          // NOT a tab stop. The panel's content is a list that manages its own roving focus,
          // and a focusable wrapper around it would add a stop that does nothing — the budget
          // `e2e/a11y.spec.ts` counts is two for the whole region.
          tabIndex={-1}
        >
          {children}
        </div>
      )}
    </aside>
  );
}

export default Drawer;
